import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import { findOptionById, filterOptions, findOptionByKey, flatten, valueEqualsOption } from './util';
import createFakeEvent from '../../lib/createFakeEvent';
import Input from '../Input/Input';
import { arrowDown, arrowUp, enter, tab } from '../../lib/keyEvents';

const defaultRenderer = (option, labelKey, valueKey) => option[labelKey] || option[valueKey] || option;

/**
 * `SelectList` is a component for displaying options that the user can select. Only one option can be selected at any given
 * time.
 *
 * The component is used for displaying autocomplete options in {@link Input} and options in {@link Select}.
 *
 * It will automatically create one selectable option from each item in the *options* array. `SelectList` supports array of
 * strings or array of objects where *labelKey* defines label property and *valueKey* defines value.
 *
 * By default, a simple vertical list is created for the items in *options*, but you can create tree like structures using
 * the *nestedKey* prop. Setting *nestedKey* to the name of the property that holds each object's child items, the
 * `SelectList` will be able to display nested tree structures.
 *
 * All items will be selectable by default, but you may use the *nestedKeySelectable* prop to point to a property in each
 * object in *options*. The option will be selectable if that property is `true`, and grayed out if the property is `false`.
 *
 * The component will handle keyboard navigation and Enter key by default, but this can be turned off if necessary.
 *
 * @property {boolean} autoFocus If set to `true`, the list will grab focus when it is mounted. This allows you to let the
 * component handle arrow up and down and enter to select item. Defaults to `false`.
 * @property {node} children Options can be provided as custom child elements instead of an *options* array. When this
 * approach is used you will have to handle click listeners etc. yourself.
 * @property {string} className The class names of your component. Will be appended to "core-select-list".
 * @property {boolean} defaultKeyHandler The component will not install a keydown listener when this is set to `false`. Use
 * this if you want your wrapper component to handle all key events. Defaults to `true`.
 * @property {boolean} isFocused The default keyhandler will only handle key presses when *isFocused* is `true` or when the
 * internal focus state is `true`.
 * @property {function} itemRenderer Sets the custom renderer for this component. Default renderer will display the
 * `labelKey` value for object arrays or string value for string arrays. The renderer will be passed the option item,
 * labelKey, valueKey and index.
 * @property {string} labelKey The name of property to use as label when *options* is an array of objects. Defaults to
 * "label".
 * @property {string} nestedKey The name of the property of your children array for each option object. Setting this prop
 * will result in the `SelectList` displaying a tree structure.
 * @property {string} nestedKeySelectable A named property in your objects in options which determines whether or not the
 * option is selectable or not. Defaults to `undefined`.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {function} onChange Called every time the user selects an item from the list. The event's target value will be
 * the object in the `options` array at the current selected index. If the item was selected using keyboard (Enter key) the
 * second parameter to *onChange* will be `true`.
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {function} onSearch Called when the user types into the search field (*showSearch* must be set to `true`).
 * @property {array} options Array of objects or strings that will be displayed as options in this component. Use the
 * *labelKey* and *valueKey* props if *options* is an array of objects.
 * @property {string} searchValue The content of the search field. Defaults to no value.
 * @property {boolean} showSearch If set to `true`, a search field will be displayed before the list. The user will be able
 * to search for items (labels) containing the value. Defaults to `false`.
 * @property {string} value The selected value/option. This should be the `option[valueKey]` when options are array of
 * objects, or the label itself when options is an array of strings. No option is selected by default.
 * @property {string} valueKey The property to use as value when *options* is an array of objects. Defaults to "value".
 *
 * @extends Input, Select, TagInput
 */
class SelectList extends PureComponent {

  static displayName = 'SelectList';

  static propTypes = {
    autoFocus: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    closeDropdown: PropTypes.func,
    defaultKeyHandler: PropTypes.bool,
    isFocused: PropTypes.bool,
    itemRenderer: PropTypes.func,
    labelKey: PropTypes.string,
    nestedKey: PropTypes.string,
    nestedKeySelectable: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onSearch: PropTypes.func,
    options: PropTypes.array,
    placeholder: PropTypes.string,
    searchValue: PropTypes.string,
    showSearch: PropTypes.bool,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    valueKey: PropTypes.string,
  };

  static defaultProps = {
    defaultKeyHandler: true,
    input: null,
    itemRenderer: defaultRenderer,
    labelKey: 'label',
    options: [],
    placeholder: 'Search',
    onBlur: () => { },
    onFocus: () => { },
    valueKey: 'value',
  };

  constructor(props) {
    super(props);
    this.refList = null;
    this.state = {
      index: this.getValueIndex(),
      numberOfOptions: this.getNumberOfOptions(props.options),
    };
  }

  componentDidMount() {
    if (this.props.autoFocus) {
      this.setFocus(true);
    }
    if (this.refList && this.refList.getElementsByClassName) {
      const items = this.refList.getElementsByClassName('selected');

      if (items && items.length && items[0].scrollIntoViewIfNeeded) {
        setTimeout(() => {
          items[0] && items[0].scrollIntoViewIfNeeded(true);
        }, 200);
      }
    }
    document.addEventListener('keydown', this.onKeyDown, true);
    document.addEventListener('mousedown', this.onClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value) {
      this.setState({ value: this.props.value });
    }
    const numberOfOptions = this.getNumberOfOptions(this.props.options);
    if (numberOfOptions !== this.getNumberOfOptions(prevProps.options)) {
      this.setState({ index: this.getValueIndex(), numberOfOptions });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.removeEventListener('mousedown', this.onClickOutside);
  }

  getFlatOptions = options => {
    const { nestedKey, nestedKeySelectable, labelKey, valueKey } = this.props;
    return nestedKey ? flatten(options, '', 0, nestedKey, nestedKeySelectable, labelKey, valueKey) : options;
  }

  getNumberOfOptions = options => {
    return this.getFlatOptions(options).length;
  }

  getValueIndex = () => {
    const { options, value, valueKey } = this.props;
    return this.getFlatOptions(options).findIndex(option => valueEqualsOption({
      option, value, valueKey
    }))
  }

  onClickOutside = e => {
    if (!this.refNode.contains(e.target)) {
      this.setFocus(false);
    }
  };

  navigateToListItem = index => {
    const item = this.refList.childNodes[index];
    if (item) {
      item.focus();
      this.setState({ index });
    }
  }

  onKeyDown = e => {
    const { closeDropdown, defaultKeyHandler, input, isFocused, nestedKey, onChange, options } = this.props;
    const { focused, index, numberOfOptions } = this.state;
    if (defaultKeyHandler && options.length) {

      if (enter(e.key) && (focused || isFocused)) {
        e.preventDefault();
        e.stopPropagation();
        if (index >= 0 && onChange) {
          const key = this.refList.childNodes[index].dataset.key;
          const option = findOptionByKey(options, key, nestedKey);

          onChange(createFakeEvent(option, e), true);
          return;
        }
      } else if (isFocused || focused) {
        if (arrowDown(e.key) || arrowUp(e.key)) {
          if (input) {
            input.setAttribute('data-keepSearchValue', true);
          }
          let newIndex = index;
          if (arrowDown(e.key)) {
            if (newIndex < numberOfOptions - 1) {
              newIndex += 1;
            }
          }
          if (arrowUp(e.key)) {
            if (newIndex >= 1) {
              newIndex -= 1;
            }
          }
          e.preventDefault();
          e.stopPropagation();
          this.navigateToListItem(newIndex);
        }

        if (tab(e.key)) {
          e.preventDefault();
          closeDropdown();
        }
      }
    }
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
  };

  onFocus = () => this.setState({ focused: true });

  onClick = e => {
    const { nestedKey, onChange, options } = this.props;
    let value;

    const element = e.target;
    if (element) {
      const index = parseInt(element.getAttribute('data-index'), 10);
      this.setState({ index });
      if (!this.state.focused) {
        this.setState({ focused: true });
      }
    }

    if (e.currentTarget.classList.contains('not-selectable')) {
      return;
    }
    if (onChange) {
      if (nestedKey) {
        value = findOptionByKey(this.props.options, e.currentTarget.dataset.key, this.props.nestedKey);
      } else {
        const index = Array.prototype.indexOf.call(this.refList.childNodes, e.currentTarget);

        value = options[index];
      }
      const event = createFakeEvent(value, e);
      setTimeout(() => { // HACK: if inside a popup menu - allow it to close itself
        onChange(event);
      }, 1);
    }
    this.setFocus(true);
  };

  setFocus = focused => {
    if (this.props.defaultKeyHandler && focused !== this.state.focused) {
      this.setState({
        focused
      }, () => {
        if (focused) {
          this.props.onFocus();
        } else {
          this.props.onBlur();
        }
      });
    }
  };

  createOptions = () => {
    const { children, itemRenderer, labelKey, nestedKey, nestedKeySelectable, options, value, valueKey } = this.props;
    let items = options;
    const nested = nestedKey && options;
    const defaultCell = itemRenderer === defaultRenderer;

    if (nested) {
      items = flatten(options, '', 0, nestedKey, nestedKeySelectable, labelKey, valueKey);
    } else if (children) {
      items = [].concat(children);
    }
    return items.map((option, index) => {
      const item = nested ? option.option : option;
      const cls = cx('select-item', nested ? `nested-cell-${option.level}` : null, {
        selected: this.state.index !== -1 ? this.state.index === index : valueEqualsOption({ option: item, value, valueKey }),
        'default-cell': defaultCell,
        'not-selectable': nestedKeySelectable ? !item[nestedKeySelectable] : false,
      });
      const key = nested ? option.key : index;

      return (
        <li
          className={cls}
          data-index={index}
          data-key={key}
          id={`item-index-${index}`}
          key={key}
          onMouseDown={this.onClick}
          tabIndex="0"
        >
          {itemRenderer(item, labelKey, valueKey, index)}
        </li>
      );
    });
  };

  scrollIntoViewIfNeeded = index => {
    const el = this.refList && this.refList.children.length > index ? this.refList.children[index] : undefined;

    if (el && el.scrollIntoViewIfNeeded) {
      el.scrollIntoViewIfNeeded(true);
    }
  };


  render() {
    const {
      autoFocus,
      children,
      className,
      closeDropdown,
      defaultKeyHandler,
      isFocused,
      itemRenderer,
      labelKey,
      nestedKey,
      nestedKeySelectable,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onSearch,
      options,
      placeholder,
      searchValue,
      showSearch,
      value,
      valueKey,
      ...rest
    } = this.props;
    const items = this.createOptions();

    return (
      <div
        {...rest}
        className={cx('core-select-list', className, { focus: this.state.focused })}
        ref={c => { this.refNode = c; }}
        onMouseMove={this.onMouseMove}
      >
        {showSearch &&
          <Input
            autoFocus
            data-preventmenuclose
            onChange={onSearch}
            onFocus={this.onFocus}
            placeholder={placeholder}
            showSearch
            showUnderline={false}
            value={searchValue}
          />
        }
        <ul
          className="core-select-list-items"
          ref={c => { this.refList = c; }}
        >
          {items}
        </ul>
      </div>
    );
  }

}

SelectList.findOptionById = findOptionById;
SelectList.filterOptions = filterOptions;

export default SelectList;
